import React, { useState, useEffect, useRef } from 'react'
import { collection, query, where, orderBy, getDocs, limit } from 'firebase/firestore'
import { db } from '../firebase'
import { useAuth } from '../contexts/AuthContext'
import AlertBadge from './AlertBadge'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js'
import { Line, Bar, Pie } from 'react-chartjs-2'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
)

export default function Analytics() {
  const { userClaims } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [customerName, setCustomerName] = useState('')
  const [analyticsData, setAnalyticsData] = useState([])
  const [summaryMetrics, setSummaryMetrics] = useState({
    totalSales: 0,
    pgSales: 0,
    cashSales: 0,
    activeDevices: 0,
    totalRefunds: 0,
    refundCount: 0
  })
  const [dateRange, setDateRange] = useState('30') // Default to last 30 days
  const chartKeyRef = useRef(0)
  
  // Cache management
  const getCacheKey = (customer, dateRange) => {
    const range = getDateRange(dateRange)
    return `analytics_${customer}_${range.start}_${range.end}`
  }
  
  const getCachedData = (cacheKey) => {
    try {
      const cached = localStorage.getItem(cacheKey)
      if (cached) {
        const { data, timestamp, summaryMetrics } = JSON.parse(cached)
        // Cache is valid for 1 hour (analytics data is historical and won't change)
        if (Date.now() - timestamp < 60 * 60 * 1000) {
          // Re-parse dates when loading from cache since JSON.stringify converts dates to strings
          const processedData = data.map(day => ({
            ...day,
            parsedDate: new Date(day.parsedDate)
          }))
          return { data: processedData, summaryMetrics }
        }
      }
    } catch (err) {
      console.warn('Failed to load cached analytics data:', err)
    }
    return null
  }
  
  const setCachedData = (cacheKey, data, summaryMetrics) => {
    try {
      localStorage.setItem(cacheKey, JSON.stringify({
        data,
        summaryMetrics,
        timestamp: Date.now()
      }))
    } catch (err) {
      // Handle quota exceeded or other localStorage errors
      console.warn('Failed to cache analytics data:', err)
      // Try to clear some old cache entries
      try {
        const keys = Object.keys(localStorage)
        const analyticsKeys = keys.filter(key => key.startsWith('analytics_'))
        if (analyticsKeys.length > 10) {
          // Remove oldest entries
          analyticsKeys.slice(0, 5).forEach(key => localStorage.removeItem(key))
          // Try caching again
          localStorage.setItem(cacheKey, JSON.stringify({
            data,
            summaryMetrics,
            timestamp: Date.now()
          }))
        }
      } catch (retryErr) {
        console.warn('Failed to clear cache and retry:', retryErr)
      }
    }
  }

  useEffect(() => {
    if (userClaims?.customer) {
      setCustomerName(userClaims.customer)
      fetchAnalyticsData(userClaims.customer)
      // Increment chart key to force re-render of charts
      chartKeyRef.current += 1
    }
  }, [userClaims, dateRange])

  // Generate date range for queries - exclude today
  const getDateRange = (days) => {
    const today = new Date()
    const endDate = new Date(today)
    endDate.setDate(today.getDate() - 1) // Exclude today - end date is yesterday
    
    const startDate = new Date(endDate)
    startDate.setDate(endDate.getDate() - parseInt(days) + 1) // Include end date in range
    
    return {
      start: formatDate(startDate),
      end: formatDate(endDate)
    }
  }

  // Format date to YYYYMMDD
  const formatDate = (date) => {
    return date.toISOString().slice(0, 10).replace(/-/g, '')
  }

  // Parse date from YYYYMMDD
  const parseDate = (dateStr) => {
    const year = dateStr.substring(0, 4)
    const month = dateStr.substring(4, 6)
    const day = dateStr.substring(6, 8)
    return new Date(`${year}-${month}-${day}`)
  }

  const fetchAnalyticsData = async (customer) => {
    if (!customer) return

    const cacheKey = getCacheKey(customer, dateRange)
    
    // Try to get cached data first
    const cachedResult = getCachedData(cacheKey)
    if (cachedResult) {
      console.log(`Loading cached analytics data for ${customer} (${cachedResult.data.length} days)`)
      setAnalyticsData(cachedResult.data)
      setSummaryMetrics(cachedResult.summaryMetrics)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const range = getDateRange(dateRange)
      
      // Convert date strings to numbers for comparison (since dates are stored as numbers)
      const startDateNum = parseInt(range.start)
      const endDateNum = parseInt(range.end)
      
      console.log(`Fetching from Firebase: customer == "${customer}", date >= ${startDateNum}, date <= ${endDateNum}`)

      const analyticsRef = collection(db, 'txn_summary')
      
      // Query using indexed fields with number comparison
      const q = query(
        analyticsRef,
        where('customer', '==', customer),
        where('date', '>=', startDateNum),
        where('date', '<=', endDateNum),
        orderBy('date', 'asc')
      )
      
      const snapshot = await getDocs(q)
      
      const data = []
      snapshot.forEach((doc) => {
        const docData = doc.data()
        data.push({
          id: doc.id,
          date: docData.date.toString(), // Convert back to string for consistency
          parsedDate: parseDate(docData.date.toString()),
          ...docData
        })
      })
      
      // Calculate metrics
      const metrics = calculateSummaryMetricsSync(data)
      
      // Cache the data and metrics
      setCachedData(cacheKey, data, metrics)
      
      setAnalyticsData(data)
      setSummaryMetrics(metrics)
      
      console.log(`Fetched and cached ${data.length} days of analytics data for ${customer}`)
    } catch (err) {
      console.error('Error fetching analytics data:', err)
      setError(`Failed to fetch analytics data: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const calculateSummaryMetrics = (data) => {
    const metrics = calculateSummaryMetricsSync(data)
    setSummaryMetrics(metrics)
  }
  
  const calculateSummaryMetricsSync = (data) => {
    let totalSales = 0
    let pgSales = 0
    let cashSales = 0
    let activeDevicesSum = 0
    let totalRefunds = 0
    let refundCount = 0

    data.forEach(day => {
      // Handle different data types and missing values
      const dayPgTotal = parseFloat(day.pgTotal) || 0
      const dayCashTotal = parseFloat(day.cashTotal) || 0
      const dayRefundTotal = parseFloat(day.refundTotal) || 0
      const dayRefundCount = parseInt(day.refundCount) || 0
      const dayLive = parseInt(day.live) || 0

      pgSales += dayPgTotal
      cashSales += dayCashTotal
      totalRefunds += dayRefundTotal
      refundCount += dayRefundCount
      activeDevicesSum += dayLive
    })

    totalSales = pgSales + cashSales

    return {
      totalSales,
      pgSales,
      cashSales,
      activeDevices: data.length > 0 ? Math.round(activeDevicesSum / data.length) : 0, // Average active devices
      totalRefunds,
      refundCount
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  // Generate SVG path for mini trend line
  const generateTrendPath = (data, width = 100, height = 30) => {
    if (data.length < 2) return ''
    
    const max = Math.max(...data)
    const min = Math.min(...data)
    const range = max - min || 1
    
    const points = data.map((value, index) => {
      const x = (index / (data.length - 1)) * width
      const y = height - ((value - min) / range) * height
      return `${x},${y}`
    })
    
    return `M ${points.join(' L ')}`
  }

  // Get trend data for different metrics
  const getTrendData = (metric) => {
    return analyticsData.slice(-7).map(day => {
      switch (metric) {
        case 'total':
          return (parseFloat(day.pgTotal) || 0) + (parseFloat(day.cashTotal) || 0)
        case 'pg':
          return parseFloat(day.pgTotal) || 0
        case 'cash':
          return parseFloat(day.cashTotal) || 0
        case 'devices':
          return parseInt(day.live) || 0
        case 'refunds':
          return parseFloat(day.refundTotal) || 0
        case 'transactions': {
          // Calculate estimated transaction count for this day
          let dayTransactions = 0
          Object.keys(day).forEach(key => {
            if (key.match(/^[a-z]{2}\d{5}$/i) && typeof day[key] === 'object') {
              const deviceData = day[key]
              const pgTotal = parseFloat(deviceData.pgTotal) || 0
              const cashTotal = parseFloat(deviceData.cashTotal) === 'NA' ? 0 : parseFloat(deviceData.cashTotal) || 0
              const totalSales = pgTotal + cashTotal
              
              if (totalSales > 0) {
                dayTransactions += Math.max(1, Math.round(totalSales / 20))
              }
            }
          })
          return dayTransactions
        }
        default:
          return 0
      }
    })
  }

  // Calculate trend direction
  const getTrendDirection = (data) => {
    if (data.length < 2) return 'neutral'
    const first = data[0] || 0
    const last = data[data.length - 1] || 0
    return last > first ? 'up' : last < first ? 'down' : 'neutral'
  }

  // Get total transaction count
  const getTotalTransactionCount = () => {
    let totalTransactions = 0
    
    analyticsData.forEach(day => {
      // Count transactions from device-level data
      Object.keys(day).forEach(key => {
        if (key.match(/^[a-z]{2}\d{5}$/i) && typeof day[key] === 'object') {
          const deviceData = day[key]
          // Estimate transactions from sales (assuming average transaction value)
          const pgTotal = parseFloat(deviceData.pgTotal) || 0
          const cashTotal = parseFloat(deviceData.cashTotal) === 'NA' ? 0 : parseFloat(deviceData.cashTotal) || 0
          const totalSales = pgTotal + cashTotal
          
          // Estimate transaction count (assuming avg transaction value of ₹20)
          if (totalSales > 0) {
            totalTransactions += Math.max(1, Math.round(totalSales / 20))
          }
        }
      })
    })
    
    return totalTransactions
  }

  // Get top devices by sales
  const getTopDevicesBySales = () => {
    const deviceSales = {}
    
    analyticsData.forEach(day => {
      // Iterate through device data (keys like vv00001, vv00002, etc.)
      Object.keys(day).forEach(key => {
        // Check if it's a device key (not customer, date, live, etc.)
        if (key.match(/^[a-z]{2}\d{5}$/i) && typeof day[key] === 'object') {
          const deviceData = day[key]
          if (!deviceSales[key]) {
            deviceSales[key] = {
              deviceId: key,
              totalSales: 0,
              pgSales: 0,
              cashSales: 0,
              daysActive: 0,
              isActive: false
            }
          }
          
          const pgTotal = parseFloat(deviceData.pgTotal) || 0
          const cashTotal = parseFloat(deviceData.cashTotal) === 'NA' ? 0 : parseFloat(deviceData.cashTotal) || 0
          
          deviceSales[key].totalSales += pgTotal + cashTotal
          deviceSales[key].pgSales += pgTotal
          deviceSales[key].cashSales += cashTotal
          deviceSales[key].isActive = deviceData.live || false
          
          if (pgTotal > 0 || cashTotal > 0) {
            deviceSales[key].daysActive += 1
          }
        }
      })
    })
    
    // Convert to array and sort by total sales
    return Object.values(deviceSales)
      .filter(device => device.totalSales > 0)
      .sort((a, b) => b.totalSales - a.totalSales)
      .slice(0, 10)
  }

  // Prepare data for daily sales trend chart
  const prepareDailySalesData = () => {
    const labels = analyticsData.map(day => 
      day.parsedDate.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })
    )
    
    const totalSalesData = analyticsData.map(day => {
      const dayPgTotal = parseFloat(day.pgTotal) || 0
      const dayCashTotal = parseFloat(day.cashTotal) || 0
      return dayPgTotal + dayCashTotal
    })

    const pgSalesData = analyticsData.map(day => parseFloat(day.pgTotal) || 0)
    const cashSalesData = analyticsData.map(day => parseFloat(day.cashTotal) || 0)
    const refundsData = analyticsData.map(day => parseFloat(day.refundTotal) || 0)

    return {
      labels,
      datasets: [
        {
          label: 'Total Sales',
          data: totalSalesData,
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          tension: 0.1
        },
        {
          label: 'PG Sales',
          data: pgSalesData,
          borderColor: 'rgb(54, 162, 235)',
          backgroundColor: 'rgba(54, 162, 235, 0.2)',
          tension: 0.1
        },
        {
          label: 'Cash Sales',
          data: cashSalesData,
          borderColor: 'rgb(255, 206, 86)',
          backgroundColor: 'rgba(255, 206, 86, 0.2)',
          tension: 0.1
        }
      ]
    }
  }

  // Prepare data for payment method pie chart
  const preparePaymentMethodData = () => {
    return {
      labels: ['PG Sales', 'Cash Sales', 'Refunds'],
      datasets: [
        {
          data: [summaryMetrics.pgSales, summaryMetrics.cashSales, summaryMetrics.totalRefunds],
          backgroundColor: [
            'rgba(54, 162, 235, 0.8)',
            'rgba(255, 206, 86, 0.8)',
            'rgba(255, 99, 132, 0.8)'
          ],
          borderColor: [
            'rgba(54, 162, 235, 1)',
            'rgba(255, 206, 86, 1)',
            'rgba(255, 99, 132, 1)'
          ],
          borderWidth: 2
        }
      ]
    }
  }

  // Prepare data for daily active devices chart
  const prepareActiveDevicesData = () => {
    const labels = analyticsData.map(day => 
      day.parsedDate.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })
    )
    
    const activeDevicesData = analyticsData.map(day => parseInt(day.live) || 0)

    return {
      labels,
      datasets: [
        {
          label: 'Active Devices',
          data: activeDevicesData,
          backgroundColor: 'rgba(75, 192, 192, 0.8)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1
        }
      ]
    }
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false
    },
    plugins: {
      legend: {
        position: 'top'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            if (context.dataset.label === 'Active Devices') {
              return context.dataset.label + ': ' + context.parsed + ' devices'
            }
            return context.dataset.label + ': ' + formatCurrency(context.parsed)
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return '₹' + value.toLocaleString()
          }
        }
      }
    }
  }

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0)
            const percentage = ((context.parsed / total) * 100).toFixed(1)
            return context.label + ': ' + formatCurrency(context.parsed) + ' (' + percentage + '%)'
          }
        }
      }
    }
  }

  if (!userClaims) {
    return (
      <div className="alert alert-info">
        <i className="fas fa-info-circle me-2"></i>
        Loading user claims...
      </div>
    )
  }

  if (!customerName) {
    return (
      <div className="alert alert-warning">
        <i className="fas fa-exclamation-triangle me-2"></i>
        No customer name found in Firebase claims. Expected claim: "customer"
      </div>
    )
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h5 className="mb-1">
            Analytics Dashboard
            <AlertBadge category="system" className="ms-2" />
          </h5>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <label className="form-label mb-0 me-2">Period:</label>
          <select 
            className="form-select form-select-sm" 
            style={{ width: 'auto' }}
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            disabled={loading}
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Summary Cards with Trend Lines */}
      {!loading && analyticsData.length > 0 && (
        <div className="row mb-4">
          <div className="col-md-6 col-lg-4 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <h6 className="text-muted text-uppercase mb-2 small">Total Sales Trend</h6>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h4 className="mb-0 text-success">{formatCurrency(summaryMetrics.totalSales)}</h4>
                  <div className={`text-${getTrendDirection(getTrendData('total')) === 'up' ? 'success' : getTrendDirection(getTrendData('total')) === 'down' ? 'danger' : 'muted'}`}>
                    <i className={`fas fa-arrow-${getTrendDirection(getTrendData('total')) === 'up' ? 'up' : getTrendDirection(getTrendData('total')) === 'down' ? 'down' : 'right'}`}></i>
                  </div>
                </div>
                <div className="mb-2">
                  <svg width="100%" height="40" className="trend-chart">
                    <path
                      d={generateTrendPath(getTrendData('total'), 300, 35)}
                      fill="none"
                      stroke="#28a745"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <small className="text-muted">Last 7 days</small>
              </div>
            </div>
          </div>

          <div className="col-md-6 col-lg-4 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <h6 className="text-muted text-uppercase mb-2 small">PG Sales Trend</h6>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h4 className="mb-0 text-primary">{formatCurrency(summaryMetrics.pgSales)}</h4>
                  <div className={`text-${getTrendDirection(getTrendData('pg')) === 'up' ? 'success' : getTrendDirection(getTrendData('pg')) === 'down' ? 'danger' : 'muted'}`}>
                    <i className={`fas fa-arrow-${getTrendDirection(getTrendData('pg')) === 'up' ? 'up' : getTrendDirection(getTrendData('pg')) === 'down' ? 'down' : 'right'}`}></i>
                  </div>
                </div>
                <div className="mb-2">
                  <svg width="100%" height="40" className="trend-chart">
                    <path
                      d={generateTrendPath(getTrendData('pg'), 300, 35)}
                      fill="none"
                      stroke="#007bff"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <small className="text-muted">Last 7 days</small>
              </div>
            </div>
          </div>

          <div className="col-md-6 col-lg-4 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <h6 className="text-muted text-uppercase mb-2 small">Cash Sales Trend</h6>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h4 className="mb-0 text-info">{formatCurrency(summaryMetrics.cashSales)}</h4>
                  <div className={`text-${getTrendDirection(getTrendData('cash')) === 'up' ? 'success' : getTrendDirection(getTrendData('cash')) === 'down' ? 'danger' : 'muted'}`}>
                    <i className={`fas fa-arrow-${getTrendDirection(getTrendData('cash')) === 'up' ? 'up' : getTrendDirection(getTrendData('cash')) === 'down' ? 'down' : 'right'}`}></i>
                  </div>
                </div>
                <div className="mb-2">
                  <svg width="100%" height="40" className="trend-chart">
                    <path
                      d={generateTrendPath(getTrendData('cash'), 300, 35)}
                      fill="none"
                      stroke="#17a2b8"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <small className="text-muted">Last 7 days</small>
              </div>
            </div>
          </div>

          <div className="col-md-6 col-lg-4 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <h6 className="text-muted text-uppercase mb-2 small">Active Devices Trend</h6>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h4 className="mb-0 text-warning">{summaryMetrics.activeDevices} devices</h4>
                  <div className={`text-${getTrendDirection(getTrendData('devices')) === 'up' ? 'success' : getTrendDirection(getTrendData('devices')) === 'down' ? 'danger' : 'muted'}`}>
                    <i className={`fas fa-arrow-${getTrendDirection(getTrendData('devices')) === 'up' ? 'up' : getTrendDirection(getTrendData('devices')) === 'down' ? 'down' : 'right'}`}></i>
                  </div>
                </div>
                <div className="mb-2">
                  <svg width="100%" height="40" className="trend-chart">
                    <path
                      d={generateTrendPath(getTrendData('devices'), 300, 35)}
                      fill="none"
                      stroke="#ffc107"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <small className="text-muted">Last 7 days</small>
              </div>
            </div>
          </div>

          <div className="col-md-6 col-lg-4 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <h6 className="text-muted text-uppercase mb-2 small">Refunds Trend</h6>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h4 className="mb-0 text-danger">{formatCurrency(summaryMetrics.totalRefunds)}</h4>
                  <div className={`text-${getTrendDirection(getTrendData('refunds')) === 'up' ? 'danger' : getTrendDirection(getTrendData('refunds')) === 'down' ? 'success' : 'muted'}`}>
                    <i className={`fas fa-arrow-${getTrendDirection(getTrendData('refunds')) === 'up' ? 'up' : getTrendDirection(getTrendData('refunds')) === 'down' ? 'down' : 'right'}`}></i>
                  </div>
                </div>
                <div className="mb-2">
                  <svg width="100%" height="40" className="trend-chart">
                    <path
                      d={generateTrendPath(getTrendData('refunds'), 300, 35)}
                      fill="none"
                      stroke="#dc3545"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <small className="text-muted">Last 7 days • {summaryMetrics.refundCount} transactions</small>
              </div>
            </div>
          </div>

          <div className="col-md-6 col-lg-4 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <h6 className="text-muted text-uppercase mb-2 small">Transaction Volume</h6>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h4 className="mb-0 text-info">{getTotalTransactionCount().toLocaleString()}</h4>
                  <div className={`text-${getTrendDirection(getTrendData('transactions')) === 'up' ? 'success' : getTrendDirection(getTrendData('transactions')) === 'down' ? 'danger' : 'muted'}`}>
                    <i className={`fas fa-arrow-${getTrendDirection(getTrendData('transactions')) === 'up' ? 'up' : getTrendDirection(getTrendData('transactions')) === 'down' ? 'down' : 'right'}`}></i>
                  </div>
                </div>
                <div className="mb-2">
                  <svg width="100%" height="40" className="trend-chart">
                    <path
                      d={generateTrendPath(getTrendData('transactions'), 300, 35)}
                      fill="none"
                      stroke="#17a2b8"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <small className="text-muted">Last {dateRange} days • Avg {Math.round(getTotalTransactionCount() / analyticsData.length)}/day</small>
              </div>
            </div>
          </div>

        </div>
      )}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div>Loading analytics data for the last {dateRange} days...</div>
        </div>
      ) : analyticsData.length === 0 ? (
        <div className="card border-0 shadow-sm">
          <div className="card-body text-center py-5">
            <i className="fas fa-chart-bar fa-3x mb-3 text-muted opacity-50"></i>
            <h5 className="text-muted">No Analytics Data Available</h5>
            <p className="text-muted mb-0">
              No transaction summary found for the selected period.<br />
              <small>Note: Transaction summaries are typically updated at the end of each day.</small>
            </p>
          </div>
        </div>
      ) : (
        <div className="row">
          {/* Simple Data Table as Fallback */}
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white">
                <h6 className="mb-0">
                  <i className="fas fa-chart-bar me-2"></i>
                  Analytics Summary ({analyticsData.length} days)
                </h6>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <h6>Recent Daily Sales</h6>
                    <div className="table-responsive">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Total Sales</th>
                            <th>Active Devices</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analyticsData.slice(-7).map((day) => {
                            const dayPgTotal = parseFloat(day.pgTotal) || 0
                            const dayCashTotal = parseFloat(day.cashTotal) || 0
                            const totalSales = dayPgTotal + dayCashTotal
                            const dayLive = parseInt(day.live) || 0

                            return (
                              <tr key={day.id}>
                                <td>
                                  {day.parsedDate.toLocaleDateString('en-IN', {
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </td>
                                <td className="text-success fw-bold">
                                  {formatCurrency(totalSales)}
                                </td>
                                <td>
                                  <span className="badge bg-secondary">{dayLive}</span>
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <h6>Payment Method Breakdown</h6>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span>PG Sales:</span>
                        <span className="fw-bold text-primary">{formatCurrency(summaryMetrics.pgSales)}</span>
                      </div>
                      <div className="progress mb-2" style={{ height: '20px' }}>
                        <div 
                          className="progress-bar bg-primary" 
                          style={{ 
                            width: `${(summaryMetrics.pgSales / summaryMetrics.totalSales * 100) || 0}%` 
                          }}
                        ></div>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span>Cash Sales:</span>
                        <span className="fw-bold text-warning">{formatCurrency(summaryMetrics.cashSales)}</span>
                      </div>
                      <div className="progress mb-2" style={{ height: '20px' }}>
                        <div 
                          className="progress-bar bg-warning" 
                          style={{ 
                            width: `${(summaryMetrics.cashSales / summaryMetrics.totalSales * 100) || 0}%` 
                          }}
                        ></div>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span>Refunds:</span>
                        <span className="fw-bold text-danger">{formatCurrency(summaryMetrics.totalRefunds)}</span>
                      </div>
                      <div className="progress mb-2" style={{ height: '20px' }}>
                        <div 
                          className="progress-bar bg-danger" 
                          style={{ 
                            width: `${(summaryMetrics.totalRefunds / summaryMetrics.totalSales * 100) || 0}%` 
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Top Devices by Sales */}
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white">
                <h6 className="mb-0">
                  <i className="fas fa-trophy me-2"></i>
                  Top Devices by Sales
                </h6>
              </div>
              <div className="card-body">
                <div className="row">
                  {getTopDevicesBySales().map((device, index) => (
                    <div key={device.deviceId} className="col-md-6 col-lg-4 mb-3">
                      <div className="card border h-100">
                        <div className="card-body p-3">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              <div className="d-flex align-items-center mb-1">
                                <span className={`badge me-2 ${index < 3 ? 'bg-warning' : 'bg-secondary'}`}>
                                  #{index + 1}
                                </span>
                                <h6 className="mb-0">{device.deviceId}</h6>
                                {device.isActive && (
                                  <i className="fas fa-circle text-success ms-2" title="Currently Active"></i>
                                )}
                              </div>
                              <div className="text-success fw-bold">
                                {formatCurrency(device.totalSales)}
                              </div>
                            </div>
                            {index < 3 && (
                              <i className={`fas fa-medal text-${index === 0 ? 'warning' : index === 1 ? 'secondary' : 'warning'}`}></i>
                            )}
                          </div>
                          
                          <div className="small text-muted">
                            <div className="d-flex justify-content-between mb-1">
                              <span>PG Sales:</span>
                              <span>{formatCurrency(device.pgSales)}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-1">
                              <span>Cash Sales:</span>
                              <span>{formatCurrency(device.cashSales)}</span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span>Active Days:</span>
                              <span>{device.daysActive}/{analyticsData.length}</span>
                            </div>
                          </div>
                          
                          {/* Sales breakdown progress bar */}
                          <div className="mt-3">
                            <div className="progress" style={{ height: '4px' }}>
                              <div 
                                className="progress-bar bg-primary" 
                                style={{ width: `${(device.pgSales / device.totalSales * 100) || 0}%` }}
                                title={`PG: ${((device.pgSales / device.totalSales * 100) || 0).toFixed(1)}%`}
                              ></div>
                              <div 
                                className="progress-bar bg-info" 
                                style={{ width: `${(device.cashSales / device.totalSales * 100) || 0}%` }}
                                title={`Cash: ${((device.cashSales / device.totalSales * 100) || 0).toFixed(1)}%`}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {getTopDevicesBySales().length === 0 && (
                    <div className="col-12">
                      <div className="text-center py-4 text-muted">
                        <i className="fas fa-info-circle fa-2x mb-2"></i>
                        <p>No device sales data available for the selected period</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}